/**
 * Modern MarqueeX Settings Page
 * Enhanced with Ant Design components and modular architecture
 */

/**
 * Styles
 */
import './style.scss';
import 'antd/dist/reset.css';

/**
 * WordPress dependencies
 */
import { useState, useEffect } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import { __ } from '@wordpress/i18n';

/**
 * Ant Design components
 */
import {
	Button,
	Tabs,
	Typography,
	Space,
	Alert,
	Row,
	Col,
	notification,
	Card
} from 'antd';
import {
	SettingOutlined,
	ThunderboltOutlined,
	ApiOutlined,
	SaveOutlined,
	ReloadOutlined,
	RocketOutlined,
	CheckCircleOutlined
} from '@ant-design/icons';

/**
 * Internal Components
 */
import BuilderCard from './components/BuilderCard';
import PerformanceCard from './components/PerformanceCard';
import CompatibilityCard from './components/CompatibilityCard';

const { Title, Text, Paragraph } = Typography;

/**
 * Main Settings Page Component
 */
const PageSettings = () => {
	const [isSaving, setIsSaving] = useState(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	// WordPress data hooks
	const { settings, builderConfigs, isLoading } = useSelect((select) => ({
		settings: select('marqueex/settings').getSettings(),
		builderConfigs: select('marqueex/settings').getBuilderConfigs(),
		isLoading: select('marqueex/settings').getLoading(),
	}));

	const { updateSettings, saveSettings } = useDispatch('marqueex/settings');


	// Handle settings changes
	const handleSettingsChange = (section, key, value) => {
		const newSettings = {
			...settings,
			[section]: {
				...settings[section],
				[key]: value
			}
		};
		updateSettings(newSettings);
		setHasUnsavedChanges(true);
	};

	// Handle builder toggle
	const handleBuilderToggle = (builder, key, value) => {
		const newSettings = {
			...settings,
			builder_support: {
				...settings.builder_support,
				[builder]: {
					...settings.builder_support[builder],
					[key]: value
				}
			}
		};
		updateSettings(newSettings);
		setHasUnsavedChanges(true);
	};

	// Handle widget/element/shortcode toggles
	const handleWidgetToggle = (builder, widget, value) => {
		const newSettings = {
			...settings,
			builder_support: {
				...settings.builder_support,
				[builder]: {
					...settings.builder_support[builder],
					widgets: {
						...settings.builder_support[builder].widgets,
						[widget]: value
					}
				}
			}
		};
		updateSettings(newSettings);
		setHasUnsavedChanges(true);
	};

	const handleElementToggle = (builder, element, value) => {
		const newSettings = {
			...settings,
			builder_support: {
				...settings.builder_support,
				[builder]: {
					...settings.builder_support[builder],
					elements: {
						...settings.builder_support[builder].elements,
						[element]: value
					}
				}
			}
		};
		updateSettings(newSettings);
		setHasUnsavedChanges(true);
	};

	const handleShortcodeToggle = (builder, shortcode, value) => {
		const newSettings = {
			...settings,
			builder_support: {
				...settings.builder_support,
				[builder]: {
					...settings.builder_support[builder],
					shortcodes: {
						...settings.builder_support[builder].shortcodes,
						[shortcode]: value
					}
				}
			}
		};
		updateSettings(newSettings);
		setHasUnsavedChanges(true);
	};

	// Handle save
	const handleSave = async () => {
		setIsSaving(true);
		try {
			await saveSettings();
			setHasUnsavedChanges(false);
			notification.success({
				message: __('Settings Saved', 'marqueex'),
				description: __('Your MarqueeX settings have been saved successfully.', 'marqueex'),
				placement: 'topRight',
			});
		} catch (error) {
			notification.error({
				message: __('Save Failed', 'marqueex'),
				description: error.message,
				placement: 'topRight',
			});
		} finally {
			setIsSaving(false);
		}
	};



	if (isLoading) {
		return (
			<div style={{ textAlign: 'center', padding: '50px' }}>
				<Space direction="vertical" size="large">
					<RocketOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
					<Text>{__('Loading MarqueeX Settings...', 'marqueex')}</Text>
				</Space>
			</div>
		);
	}

	// Tab items configuration
	const tabItems = [
		{
			key: 'builders',
			label: (
				<Space>
					<SettingOutlined />
					{__('Builder Support', 'marqueex')}
				</Space>
			),
			children: (
				<div>
					{/* <Card
						style={{
							marginBottom: 24,
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
							border: 'none',
							color: 'white'
						}}
					>
						<Row align="middle">
							<Col flex="auto">
								<Title level={4} style={{ color: 'white', margin: 0 }}>
									{__('Page Builder Integration', 'marqueex')}
								</Title>
								<Paragraph style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>
									{__('Enable MarqueeX integration with your favorite page builders', 'marqueex')}
								</Paragraph>
							</Col>
							<Col>
								<CheckCircleOutlined style={{ fontSize: '32px', color: 'white' }} />
							</Col>
						</Row>
					</Card> */}

					<Row gutter={[16, 16]}>
						{builderConfigs && Object.keys(builderConfigs).map(builder => (
							<Col xs={24} lg={12} key={builder}>
								<BuilderCard
									builder={builder}
									config={builderConfigs[builder]}
									settings={settings.builder_support?.[builder] || {}}
									onToggle={handleBuilderToggle}
									onWidgetToggle={handleWidgetToggle}
									onElementToggle={handleElementToggle}
									onShortcodeToggle={handleShortcodeToggle}
								/>
							</Col>
						))}
					</Row>
				</div>
			)
		},
		{
			key: 'performance',
			label: (
				<Space>
					<ThunderboltOutlined />
					{__('Performance', 'marqueex')}
				</Space>
			),
			children: (
				<PerformanceCard
					settings={settings.performance || {}}
					onUpdate={(key, value) => handleSettingsChange('performance', key, value)}
				/>
			)
		},
		{
			key: 'compatibility',
			label: (
				<Space>
					<ApiOutlined />
					{__('Compatibility', 'marqueex')}
				</Space>
			),
			children: (
				<CompatibilityCard
					settings={settings.compatibility || {}}
					onUpdate={(key, value) => handleSettingsChange('compatibility', key, value)}
				/>
			)
		}
	];

	return (
		<div className="marqueex-admin-settings">
			{/* Header Section */}
			{/* <Card
				style={{
					marginBottom: 24,
					background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
					border: 'none',
					textAlign: 'center'
				}}
			>
				<Space direction="vertical" size="large">
					<RocketOutlined style={{ fontSize: '48px', color: 'white' }} />
					<div>
						<Title level={2} style={{ color: 'white', margin: 0 }}>
							{__('MarqueeX Settings', 'marqueex')}
						</Title>
						<Paragraph style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px', margin: 0 }}>
							{__('Configure your marquee sliders, news tickers, and infinite scrolling effects', 'marqueex')}
						</Paragraph>
					</div>
				</Space>
			</Card> */}

			{/* Main Content */}
			<Tabs
				defaultActiveKey="builders"
				items={tabItems}
				size="large"
				style={{ background: 'white', borderRadius: '8px', padding: '0px 24px 24px 24px' }}
			/>

			{/* Footer Actions */}
			<Card style={{ marginTop: 24, textAlign: 'center' }}>
				<Space size="large">
					<Button
						type="primary"
						size="large"
						icon={<SaveOutlined />}
						loading={isSaving}
						onClick={handleSave}
						disabled={!hasUnsavedChanges}
					>
						{__('Save Settings', 'marqueex')}
					</Button>
					<Button
						size="large"
						icon={<ReloadOutlined />}
						onClick={() => window.location.reload()}
					>
						{__('Reset', 'marqueex')}
					</Button>
				</Space>

				{hasUnsavedChanges && (
					<Alert
						message={__('You have unsaved changes', 'marqueex')}
						type="warning"
						showIcon
						style={{ marginTop: 16 }}
					/>
				)}
			</Card>
		</div>
	);
};

export default PageSettings;
